// src/TagManagement.js
import React, { useState, useEffect, useCallback } from 'react'; // (1. เพิ่ม useCallback)
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext'; // (2. Import useAuth - ถูกต้อง)
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// --- (3. ลบฟังก์ชัน getAuthHeaders เก่า (บรรทัด 11-19) ทิ้งไป) ---
// const getAuthHeaders = () => {
//   ... (โค้ดเก่าที่ใช้ user.token) ...
// };
// --------------------------------------------------------

function TagManagement() {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // States สำหรับฟอร์ม
  const [tagName, setTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);

  // States สำหรับ Modal ลบ
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState(null);

  const navigate = useNavigate();
  // --- (4. ดึง getAuthHeaders "ตัวใหม่" มาจาก useAuth) ---
  const { user, getAuthHeaders } = useAuth();

  // --- (5. แก้ไข fetchTags) ---
  const fetchTags = useCallback(async () => {
    setIsLoading(true);
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.get(`${API_URL}/tags`, getAuthHeaders());
      // (เราใช้ 'label' เป็นชื่อแสดงผล และ 'id' เป็น key)
      setTags(response.data.map(tag => ({ id: tag.id, name: tag.label })));
    } catch (err) {
      toast.error("ไม่สามารถโหลด Tags ได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]); // (6. เพิ่ม getAuthHeaders ใน dependency array)

  useEffect(() => {
    fetchTags();
  }, [fetchTags]); // (fetchTags ถูกสร้างด้วย useCallback แล้ว)

  // (ฟังก์ชัน) เคลียร์ฟอร์ม
  const clearForm = () => {
    setTagName('');
    setEditingTagId(null);
  };

  // --- (7. แก้ไข handleSubmit) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tagName) {
      toast.error("กรุณากรอกชื่อ Tag");
      return;
    }
    
    const payload = { name: tagName }; 

    try {
      if (editingTagId) {
        // --- โหมดแก้ไข (UPDATE) ---
        // (เรียกใช้ getAuthHeaders() จาก useAuth)
        const response = await axios.put(`${API_URL}/tags/${editingTagId}`, payload, getAuthHeaders());
        toast.success(response.data.msg);
      } else {
        toast.error("การสร้าง Tag ใหม่ จะทำผ่านหน้า 'เพิ่ม/แก้ไขเอกสาร'");
      }
      clearForm();
      fetchTags(); // โหลดใหม่
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };

  // (ฟังก์ชัน) เมื่อคลิก "แก้ไข"
  const handleEditClick = (tag) => {
    setEditingTagId(tag.id);
    setTagName(tag.name);
    window.scrollTo(0, 0); // เลื่อนจอขึ้นบน
  };

  // (ฟังก์ชัน) "เปิด" Modal ลบ
  const handleOpenDeleteModal = (tag) => {
    setDeletingTag(tag);
    setIsDeleteModalOpen(true);
  };

  // --- (8. แก้ไข handleConfirmDelete) ---
  const handleConfirmDelete = async () => {
    if (!deletingTag) return;
    try {
      // (เรียกใช้ getAuthHeaders() จาก useAuth)
      const response = await axios.delete(`${API_URL}/tags/${deletingTag.id}`, getAuthHeaders());
      toast.success(response.data.msg);
      fetchTags();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingTag(null);
    }
  };

  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin/documents')}
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-300"
        >
          &larr; ย้อนกลับ
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* --- ส่วนที่ 1: ฟอร์มแก้ไข --- */}
          <div className="lg:col-span-1">
            <div className="w-full bg-white rounded-lg shadow-xl p-8 sticky top-28">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
                {editingTagId ? 'แก้ไข Tag' : 'จัดการ Tag'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="tagName" className="block text-base font-medium text-gray-700">
                    {editingTagId ? 'แก้ไขชื่อ Tag เป็น:' : 'เลือก Tag จากตารางเพื่อแก้ไข'}
                  </label>
                  <input
                    type="text" id="tagName" value={tagName}
                    onChange={(e) => setTagName(e.target.value)}
                    required
                    disabled={!editingTagId} // (ปิดช่องนี้ถ้ายังไม่เลือก)
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    * การสร้าง Tag ใหม่ ให้ไปที่หน้า "เพิ่ม/แก้ไขเอกสาร"
                  </p>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={!editingTagId} // (ปิดปุ่มถ้ายังไม่เลือก)
                    className="w-full px-4 py-3 font-semibold text-xl text-white rounded-md transition duration-300 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    บันทึกการแก้ไข
                  </button>
                  {editingTagId && (
                    <button
                      type="button"
                      onClick={clearForm}
                      className="w-full mt-3 px-4 py-2 font-semibold text-base text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                      ยกเลิกการแก้ไข
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* --- ส่วนที่ 2: ตารางแสดง Tags --- */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-3">
              Tags ที่มีอยู่ทั้งหมด
            </h2>
            <div className="overflow-x-auto">
              {isLoading ? <p>Loading tags...</p> : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase w-20">NO.</th>
                      <th className="px-6 py-3 text-left text-base font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-right text-base font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tags.map((tag, index) => (
                      <tr key={tag.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-700">{index + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-900 font-medium">{tag.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-lg font-medium space-x-4">
                          <button
                            onClick={() => handleEditClick(tag)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            แก้ไข
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(tag)}
                            className="text-red-600 hover:text-red-900"
                          >
                            ลบ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- Modal สำหรับยืนยันการลบ --- */}
      {isDeleteModalOpen && deletingTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">
              ยืนยันการลบ Tag
            </h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบ Tag: <br/>
              <strong className="text-red-600">{deletingTag.name}</strong>?
            </p>
            <p className="text-base text-gray-600">
              (เอกสารทั้งหมดที่เคยใช้ Tag นี้ จะถูก "ตัดการเชื่อมโยง" ออก)
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition duration-300"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TagManagement;